<template>
    <div class="mb-3">
        <h3>Review</h3>

        <div class="row mb-3">
            <div class="col-12 col-md-3">
                <strong>Aircraft</strong>
            </div>
            <div class="col-12 col-md-9">
                {{ store.aircraftTailNumber }}<br />
                {{ store.aircraftYear }}
                {{ store.aircraftMake }}
                {{ store.aircraftModel }}
            </div>
        </div>

        <div class="row mb-3">
            <div class="col-12 col-md-3">
                <strong>Engines</strong>
            </div>
            <div class="col-12 col-md-9">
                {{ store.aircraftEngines == 1 ? 'Single-engine' : 'Multi-engine' }}
            </div>
        </div>

        <div class="row mb-3">
            <div class="col-12 col-md-3">
                <strong>Arrival Date</strong>
            </div>
            <div class="col-12 col-md-9">
                {{ store.arrivalDate }}
            </div>
        </div>

        <div class="row mb-3">
            <div class="col-12 col-md-3">
                <strong>Departure Date</strong>
            </div>
            <div class="col-12 col-md-9">
                {{ store.departureDate }}
            </div>
        </div>

        <div class="row mb-3">
            <div class="col-12 col-md-3">
                <strong>Contact</strong>
            </div>
            <div class="col-12 col-md-9">
                {{ store.contactName }}<br />
                {{ store.contactPhone }}<br />
                {{ store.contactEmail }}
            </div>
        </div>

        <div class="row mb-3">
            <div class="col-12 col-md-3">
                <strong>Amount Due</strong>
            </div>
            <div class="col-12 col-md-9">
                ${{ store.estimatedFee}} USD
            </div>
        </div>

        <div class="row mb-3">
            <div class="col-12 col-md-3">
                <strong>Billing</strong>
            </div>
            <div class="col-12 col-md-9">
                {{ store.paymentFirstName }} {{ store.paymentLastName }}<br />
                {{ store.paymentAddress1 }}<br />
                {{ store.paymentLocality }}, {{ store.paymentRegion }} {{ store.paymentPostalCode }} {{ store.paymentCountry }}
            </div>
        </div>

        <div class="mb-3">

            <strong><u>Transient Parking Terms and Conditions</u></strong>

            <ul>
                <li>
                    <strong>Short Term Use Only</strong>
                    <p>
                        Transient Parking = Daily Rates Apply.<br />
                        Based Aircraft Parking = Monthly Rates Apply (requires a current month-to-month tie-down License, inquire with the Port at <a href="mailto:HIOtiedowns@portofportland.com">HIOtiedowns@portofportland.com</a>).
                    </p>
                </li>

                <li>
                    <strong>Secure Your Aircraft</strong>
                    <p>
                        Pilots are responsible for properly securing their aircraft. The Airport is not liable for damage caused by weather, improper tie downs, damage caused to aircraft by others, or any other damage caused to aircraft while aircraft is secured on Airport property.
                    </p>
                </li>

                <li>
                    <strong>Fees</strong>
                    <p>
                        Transient parking fees apply as posted and must be paid before departure.
                    </p>
                </li>

                <li>
                    <strong>Follow Airport Rules</strong>
                    <p>
                        All pilots must comply with FAA regulations and instructions from Airport Operations staff.
                    </p>
                </li>

                <li>
                    <strong>Aircraft Condition</strong>
                    <p>
                        Only airworthy aircraft may be parked in transient spaces. Aircraft that leak fluids, have flat tires, or otherwise appear unsafe, as determined by Airport Operations staff, may not park in transient parking spaces. Any pilot and/or aircraft owner whose aircraft leaks fluids or causes damage to pavement while parked shall be responsible for all associated clean-up and repair costs.
                    </p>
                </li>

                <li>
                    <strong>Restricted Activities</strong>
                    <p>
                        No engine run ups, washing, or maintenance in transient tie down areas unless authorized by Airport Operations staff.
                    </p>
                </li>

                <li>
                    <strong>Airport Right to Relocate</strong>
                    <p>
                        The Airport may move any aircraft as needed for safety, operational, or emergency reasons.
                    </p>
                </li>

                <li>
                    <strong>Abandoned Aircraft</strong>
                    <p>
                        Any aircraft left beyond allowed time limits or without payment may be relocated or impounded at the pilot’s expense.
                    </p>
                </li>

            </ul>
        </div>

        <div class="form-group col-sm-12">
            <div class="d-flex flex-column align-items-center">
                <recaptcha ref="recaptchaEl" :site-key="store.recaptchaSiteKey" @solved="recaptchaCallback"></recaptcha>
            </div>
        </div>

        <div v-if="paymentError" class="bg-white">
            <div class="text-danger">There was an error processing your payment. See details below:</div>
            <div class="text-danger">Status: {{ paymentErrorStatus }}</div>
            <div class="text-danger">Reason: {{ paymentErrorReason }}</div>
            <div class="text-danger">Message: {{ paymentErrorMessage }}</div>
        </div>

        <div class="text-center">
            <button type="button" class="btn btn-outline-primary me-3" @click="goBackToPage('PAYMENT')">Back</button>
            <button type="button" class="btn btn-primary" @click="submitPayment" :disabled="!submitEnabled">Submit Payment <i v-if="submittingPayment" class="fa-solid fa-spinner fa-spin"></i></button>
        </div>
    </div>
</template>

<script setup>
    import { computed, ref } from 'vue';
    import { useStore } from './store';
    import recaptcha from '../../../components/recaptcha';

    const emit = defineEmits(['goToPage', 'goBackToPage']);

    const recaptchaResult = ref('');
    const recaptchaPassed = ref(false);
    const recaptchaEl = ref(null);

    const submittingPayment = ref(false);

    const paymentError = ref(false);
    const paymentErrorStatus = ref('');
    const paymentErrorReason = ref('');
    const paymentErrorMessage = ref('');

    const store = useStore();

    const submitEnabled = computed(() => {
        return (recaptchaPassed.value && !submittingPayment.value);
    });

    function goToPage(pageName) {
        emit('goToPage', pageName);
    }

    function goBackToPage(pageName) {
        emit('goBackToPage', pageName);
    }

    function recaptchaCallback(newState) {
        recaptchaPassed.value = !!newState;

        recaptchaResult.value = newState ? recaptchaEl.value.getResponse() : null;
    }

    function submitPayment() {

        var formData = new FormData();

        formData.append('g-recaptcha-response', recaptchaResult.value);

        formData.append('tailNumber', store.aircraftTailNumber);
        formData.append('aircraftYear', store.aircraftYear);
        formData.append('aircraftMake', store.aircraftMake);
        formData.append('aircraftModel', store.aircraftModel);
        formData.append('aircraftEngines', store.aircraftEngines);
        formData.append('arrivalDate', store.arrivalDate);
        formData.append('departureDate', store.departureDate);
        formData.append('contactName', store.contactName);
        formData.append('contactPhone', store.contactPhone);
        formData.append('contactEmail', store.contactEmail);
        formData.append('paymentFirstName', store.paymentFirstName);
        formData.append('paymentLastName', store.paymentLastName);
        formData.append('paymentAddress1', store.paymentAddress1);
        formData.append('paymentLocality', store.paymentLocality);
        formData.append('paymentRegion', store.paymentRegion);
        formData.append('paymentPostalCode', store.paymentPostalCode);
        formData.append('paymentCountry', store.paymentCountry);
        formData.append('jwt', store.flexResponse);
        formData.append('emailToken', store.emailToken);

        submittingPayment.value = true;

        $.ajax({
            type: "POST",
            url: store.submitPaymentUrl,
            data: formData,
            processData: false,
            contentType: false,
            success: function (response) {

                if (response.Status == 'AUTHORIZED') {
                    store.paymentId = response.ResponseId;
                    submittingPayment.value = false;
                    goToPage('FINISH');
                }
                else {
                    paymentError.value = true;
                    paymentErrorStatus.value = response.Status;
                    paymentErrorReason.value = response.Reason;
                    paymentErrorMessage.value = response.Message;
                    submittingPayment.value = false;
                }
            }
        });
    }

</script>
